import React, { useEffect, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Box,
  List,
  TextField,
  InputAdornment,
  CircularProgress,
  Avatar,
  Typography,
  Container,
  useMediaQuery,
  useTheme,
  Tooltip,
  Paper,
  IconButton,
  Skeleton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Badge, Button, ListItem, styled } from "@mui/material";
import { useTranslation } from "react-i18next"; // Import the translation hook
import colors from "colors/color";
import { FormControl as Input } from "react-bootstrap";
import searchIcon from "../../assets/img/search_icon.svg";
import useUserId from "hooks/useUserId";
import ClearIcon from "@mui/icons-material/Clear";
import { COLORS } from "colors/color";

const CompanyLogo = styled(Box)(({ theme, logo }) => ({
  overflow: "hidden",
  minWidth: "48px",
  minHeight: "48px",
  maxWidth: "48px",
  maxHeight: "48px",
  background:
    "linear-gradient(135deg, rgba(0, 23, 231, 0.5) -54.69%, rgba(255, 204, 248, 0.5) 66.41%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 14,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  fontSize: "0.75rem",
  textAlign: "center",
}));
const SearchInput = styled(Input)({
  border: "none",
  "&.MuiInput-root": {
    border: "none",
    "&:before, &:after": {
      display: "none",
    },
  },
  "& .MuiInput-input": {
    padding: 0,
  },
});
const SearchContainer = styled(Paper)(({ theme, userRole }) => ({
  display: "flex",
  flexDirection: {
    xs: "column",
    md: "row",
  },
  border: "1px solid #D9D9D9",
  alignItems: "center",
  gap: "8px",
  padding: "12px 18px",
  borderRadius: 20,
  boxShadow: "none",
  height: "auto",
  minWidth: 50,
  width: userRole === "c" ? "50%" : "calc(100% - 32px)",
  maxWidth: userRole === "c" ? "100px" : "100%",
  margin: "16px 16px 8px 16px",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "none",
  },
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    height: "auto",
    width: "calc(100% - 32px)",
  },
}));

const InputWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "4px 8px",
  flex: 1,
  width: "100%",
  borderLeft: (props) => (props.isSecondInput ? "1px solid #f1f1f1" : "none"),
  [theme.breakpoints.down("md")]: {
    borderLeft: "none",
    borderTop: (props) => (props.isSecondInput ? "1px solid #f1f1f1" : "none"),
    padding: "8px",
  },
}));
const MessageTypography = styled(Typography)(({ theme }) => ({
  fontSize: "0.8rem",
  color: COLORS.Button_OnHold,
  marginTop: theme.spacing(0.5),
  opacity: 0.8,
  fontStyle: "italic",
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  lineHeight: "1.2em",
  maxHeight: "2.4em",
}));

const SearchBox = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: 1,
  backgroundColor: "white",
  borderRadius: "50px",
  boxShadow: "0 4px 20px 0px rgba(0, 0, 0, 0.08)",
  width: "100%",
  zIndex: 100,
  overflow: "hidden",
  fontFamily: "Poppins",
  marginTop: "9%",
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  padding: "1px 16px",
  width: "100%",
  marginBottom: "12px",
  // border:'1px solid #000',
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#0085FF",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "#004F98",
  },
}));

const StyledButton = styled(Button)(({ theme, isSelected, isLastClicked }) => ({
  width: "100%",
  height: "72px",
  backgroundColor: COLORS.white,
  color: "#004F98",
  fontWeight: 600,
  fontSize: "1rem",
  padding: "9px 16px",
  borderRadius: "12px",
  textTransform: "none",
  justifyContent: "space-between",
  border: "1px solid #D9D9D9",
  // boxShadow: isLastClicked
  //   ? "0 0 5px 0 #0085FF"
  //   : "0 4px 20px 0px rgba(0, 0, 0, 0.08)",
  transition: "all 0.3s ease",

  "&:hover ": {
    background: "linear-gradient(35deg, #0085FF 0%, #004F98 40%)",
    color: COLORS.white,
    boxShadow: "0 8px 20px 0px rgba(0, 0, 0, 0.16)",
  },
  ...(isSelected && {
    backgroundColor: "#004F98",
    color: COLORS.white,
    boxShadow: "0 0 5px 0 #0085FF",
  }),
  "&:hover .hover-name": {
    fontSize: "12px",
    transition: "all 0.2s ease-in-out",
    color: "#fff",
  },
  "&:hover .hover-company": {
    transition: "all 0.3s ease-in-out",
    color: "#fff",
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  fontFamily: "Poppins",
  textTransform: "none",
  fontSize: "1rem",
  width: "80%",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "50px",
    "& fieldset": {
      borderColor: "transparent",
      borderRadius: 0,
    },
    "&:hover fieldset": {
      borderColor: "transparent",
    },
    "&.Mui-focused fieldset": {
      borderColor: "transparent",
    },
  },
}));

// Add ConversationSkeleton component
const ConversationSkeleton = () => (
  <StyledListItem disablePadding>
    <Box
      sx={{
        width: "100%",
        height: "72px",
        display: "flex",
        alignItems: "center",
        gap: 2,
        p: 2,
        borderRadius: "12px",
        border: "1px solid #D9D9D9",
      }}
    >
      <Skeleton variant="circular" width={48} height={48} />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="text" width="40%" height={24} />
        <Skeleton variant="text" width="30%" height={16} sx={{ mt: 0.5 }} />
        <Skeleton variant="text" width="60%" height={16} sx={{ mt: 0.5 }} />
      </Box>
    </Box>
  </StyledListItem>
);

const Contacts = ({
  callStatus = "inactive",
  loading = false,
  searchTerm = "",
  setSearchTerm,
  filteredConversations = [],
  senderId,
  notificationCounts = {},
  handleConversationClick,
  conversationDates,
  conversations,
  loadMoreConversations,
  hasMoreConversations,
  isLoadingMore,
}) => {
  const { t } = useTranslation(); // Initialize the translation hook
  const [lastClickedId, setLastClickedId] = useState(null);
  const isHovered = useRef(null);
  const theme = useTheme();
  const isLaptop = useMediaQuery(theme.breakpoints.up("lg"));
  const isTablette = useMediaQuery(
    "(min-width: 600px) and (max-width: 1566px)"
  );
  const isMobile = useMediaQuery(theme.breakpoints.down("sx"));
  const charLimit = isTablette ? 20 : 50;
  const user = useUserId();
  const userRole = user.userRole;
  const userRole2 = user.role;
  const [messageLimit, setMessageLimit] = useState(100);
  const containerRef = useRef(null);
  const historyBoxRef = useRef(null);

  // Handle scroll event for infinite scroll
  const handleScroll = useCallback((e) => {
    const element = e.target;
    // Calculate how close we are to the bottom (in pixels)
    const distanceToBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    
    // If we're within 200px of the bottom, trigger the load more function
    if (distanceToBottom < 200 && !isLoadingMore && hasMoreConversations) {
      console.log(`Scroll threshold reached (${distanceToBottom}px from bottom), loading more conversations`);
      loadMoreConversations();
    }
  }, [isLoadingMore, hasMoreConversations, loadMoreConversations]);

  // Set up scroll event listener
  useEffect(() => {
    const historyBox = historyBoxRef.current;
    if (historyBox) {
      historyBox.addEventListener('scroll', handleScroll);
      return () => {
        historyBox.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll]);

  const getLastMessage = (userId) => {
    // console.log('conversations in contacts :',conversations)
    const conversation = conversations.find(
      (conv) => conv.other_user.id === userId
    );
    return conversation?.last_message?.content || t("noMessage"); // Translated text
  };

  useEffect(() => {
    const updateMessageLimit = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const estimatedLimit = Math.floor(containerWidth / 8);
        setMessageLimit(Math.max(20, Math.min(100, estimatedLimit)));
      }
    };

    updateMessageLimit();

    const resizeObserver = new ResizeObserver(updateMessageLimit);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
    };
  }, []);

  const truncateMessage = (text) => {
    if (!text) return "";
    return text.length > messageLimit
      ? `${text.slice(0, messageLimit)}...`
      : text;
  };

  const handleClick = (userId) => {
    console.log("Clicked user ID:", userId);
    // userId = 8;
    setLastClickedId(userId);
    
    handleConversationClick?.(userId);
  };
//  useEffect(()=>{
//   console.log('user role :',user)
//  },[userRole])

  return (
    <Box sx={{ height: "100%", width: "100%" }}>
      {callStatus !== "active" && (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            p: 1,
            display: "flex",
            flexDirection: "column",
            // marginLeft:4
          }}
        >
          {userRole2 === "c" ?<Typography
            variant="h4"
            component="h1"
            sx={{
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: "40px",
              lineHeight: "100%",
              color: colors.primary,
              marginLeft: 4,
              marginBottom: 4,
            }}
          >
            {t("chat")} {/* Translated title */}
          </Typography> : null}
          
          {/* <SearchBox>
            <StyledTextField
              placeholder={t("searchPlaceholder")} // Translated placeholder
              value={searchTerm}
              onChange={(e) => setSearchTerm?.(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ ml: 2 }}>
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              type="submit"
              sx={{
                textTransform: "none",
                fontFamily: "Poppins",
                borderRadius: "100px",
                margin: 0.5,
                paddingX: 5,
                backgroundColor: "#0085FF",
              }}
            >
              {t("search")} 
            </Button>
          </SearchBox> */}

          <SearchContainer userRole={userRole}>
            <InputWrapper sx={{ flex: 1 }}>
              <img
                src={searchIcon}
                alt="Search Icon"
                style={{ 
                  marginRight: "8px",
                  width: "20px",
                  height: "20px",
                  opacity: 0.6
                }}
              />
              <SearchInput
                placeholder={t("serachBar.searchByName")}
                fullWidth
                value={searchTerm}
                onChange={(e) => setSearchTerm?.(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    // Search is automatic as user types, this just prevents form submission
                  }
                }}
                sx={{
                  fontFamily: "Poppins",
                  fontSize: 14,
                  color: COLORS.primary,
                  "::placeholder": {
                    color: COLORS.Button_OnHold,
                    opacity: 0.8,
                  },
                  transition: "all 0.2s ease",
                }}
              />
              {searchTerm && (
                <IconButton 
                  size="small" 
                  onClick={() => setSearchTerm?.('')}
                  sx={{ 
                    ml: 1,
                    color: COLORS.Button_OnHold,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      color: COLORS.primary,
                      backgroundColor: "rgba(0, 133, 255, 0.04)",
                    }
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
            </InputWrapper>
            <Button
              variant="contained"
              onClick={() => {
                // Search is automatic, this just focuses the search input
                document.querySelector('input[placeholder="' + t("searchByName") + '"]')?.focus();
              }}
              sx={{
                minWidth: 100,
                height: 45,
                borderRadius: "12px",
                padding: "8px 16px",
                gap: "10px",
                backgroundColor: colors.primary,
                textTransform: "none",
                fontWeight: 500,
                fontSize: 14,
                fontFamily: "Poppins",
                boxShadow: "none",
                // boxShadow: "0px 4px 12px rgba(0, 133, 255, 0.25)",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: COLORS.Button_Color_hover,
                  boxShadow: "none",
                }
              }}
            >
              {t("search")}
            </Button>
          </SearchContainer>
          <List
            ref={historyBoxRef}
            sx={{
              width: "100%",
              mt: 2,
              flex: 1,
              overflow: "auto",
              transition: "all 0.3s ease",
              "&::-webkit-scrollbar": {
                width: "8px",
                opacity: 0,
                transition: "all 0.3s ease",
              },
              "&:hover": {
                "&::-webkit-scrollbar": {
                  opacity: 1,
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "transparent",
                  transition: "opacity 0.3s ease",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "linear-gradient(45deg, #0085FF, #004F98)",
                  borderRadius: "4px",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
                  transition: "background 0.3s ease, box-shadow 0.3s ease",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "linear-gradient(45deg, #004F98, #002B5C)",
                },
              },
            }}
          >
            {loading ? (
              // Show skeletons while loading
              [...Array(5)].map((_, index) => (
                <ConversationSkeleton key={index} />
              ))
            ) : (
              // Show actual conversations when loaded
              Array.isArray(filteredConversations) &&
              filteredConversations.map((user) => {
                if (!user?.id) return null;
                const isLastClicked = user.id === lastClickedId;
                const lastMessageText = getLastMessage(user.id);
                const unreadCount = (notificationCounts && notificationCounts[user.id]) || 0;

                return (
                  <StyledListItem
                    key={user.id}
                    disablePadding
                    selected={user.id === senderId}
                  >
                    <StyledButton
                      onClick={() => handleClick(user.id)}
                      isSelected={user.id === senderId}
                      isLastClicked={isLastClicked}
                      disableRipple
                      
                    >
                        <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          width: "100%",
                        }}
                      >
                        <CompanyLogo
                          logo={user.image || user.company || user.first_name}
                        >
                          {user.image ? (
                            <img src={user.image} alt="Company Logo" />
                          ) : (
                            <Typography
                              sx={{
                                color: "white",
                                textAlign: "center",
                                fontFamily: "Plain",
                                fontWeight: 600,
                                fontSize: "4.88px",
                                lineHeight: "100%",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: "100%",
                              }}
                            >
                              {user.company}
                            </Typography>
                          )}
                        </CompanyLogo>
                          <Box
                          sx={{
                            textAlign: "left",
                            flex: 1,
                            minWidth: 0,
                            maxWidth: "calc(100% - 64px)",
                          }}
                        >
                          <Typography
                            className="hover-name"
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.95rem",
                              lineHeight: 1.2,
                              color: "#004F98",
                            }}
                            noWrap
                          >
                            {user.first_name || ""} {user.last_name || ""}
                          </Typography>
                          <Typography
                            className="hover-company"
                            sx={{
                              fontSize: "0.75rem",
                              color: "#00000080",
                            }}
                            noWrap
                          >
                            {user.company || t("noCompany")}{" "}
                            {/* Translated text */}
                          </Typography>
                          <Tooltip
                            title={lastMessageText}
                            placement="bottom-start"
                          >
                            <MessageTypography className="hover-company">
                              {truncateMessage(lastMessageText)}
                            </MessageTypography>
                          </Tooltip>
                        </Box>
                          {unreadCount > 0 && (
                            <Box
                              sx={{
                                ml: 'auto',
                                backgroundColor: colors.primary,
                                color: '#fff',
                                borderRadius: '12px',
                                minWidth: 22,
                                height: 22,
                                px: 0.75,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px',
                                fontWeight: 700,
                              }}
                            >
                              {unreadCount > 99 ? '99+' : unreadCount}
                            </Box>
                          )}
                      </Box>
                    </StyledButton>
                  </StyledListItem>
                );
              })
            )}
            
            {/* Load More Button */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              width: '100%',
              my: 2
            }}>
              {hasMoreConversations && !loading && (
                <Button 
                  onClick={loadMoreConversations}
                  variant="outlined"
                  disabled={isLoadingMore}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '8px',
                    fontFamily: 'Poppins',
                    color: colors.primary,
                    borderColor: colors.primary,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 133, 255, 0.04)',
                      borderColor: colors.primary
                    }
                  }}
                  startIcon={isLoadingMore ? <CircularProgress size={16} /> : null}
                >
                  {isLoadingMore ? t("loading") : t("loadMore")}
                </Button>
              )}
            </Box>

            {/* Loading indicator for pagination */}
            {isLoadingMore && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                p: 2 
              }}>
                <CircularProgress size={24} color="primary" />
              </Box>
            )}
          </List>
          {filteredConversations.length === 0 && !loading && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              height: '100px',
              color: 'text.secondary'
            }}>
              <Typography>
                {t("noConversations")}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

Contacts.propTypes = {
  callStatus: PropTypes.string,
  loading: PropTypes.bool,
  searchTerm: PropTypes.string,
  setSearchTerm: PropTypes.func,
  filteredConversations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      avatar: PropTypes.string,
      lastMessage: PropTypes.string,
    })
  ),
  senderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  notificationCounts: PropTypes.object,
  handleConversationClick: PropTypes.func,
  loadMoreConversations: PropTypes.func,
  hasMoreConversations: PropTypes.bool,
  isLoadingMore: PropTypes.bool,
};

export default Contacts;